import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  min,
  max,
  required = false,
  className = '',
  label,
  placeholder
}) => {
  const [isOldAndroid, setIsOldAndroid] = useState(false);
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  // Parse min and max dates
  const minDate = min ? new Date(min) : new Date();
  const maxDate = max ? new Date(max) : new Date(new Date().getFullYear() + 10, 11, 31);
  
  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  // Detect old Android browser
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf('android') > -1;
    const versionMatch = userAgent.match(/android\s([0-9\.]*)/);
    let androidVersion = 0;
    
    if (versionMatch && versionMatch[1]) {
      androidVersion = parseFloat(versionMatch[1]);
    }
    
    // Consider Android versions below 7 as "old"
    setIsOldAndroid(isAndroid && androidVersion < 7);
  }, []);

  // Initialize day, month, year from value
  useEffect(() => {
    if (value) {
      const [yearVal, monthVal, dayVal] = value.split('-');
      setYear(yearVal);
      setMonth(monthVal);
      setDay(dayVal);
    }
  }, [value]);

  // Update the date when day, month, or year changes
  useEffect(() => {
    if (day && month && year) {
      const newDate = `${year}-${month}-${day}`;
      onChange(newDate);
    }
  }, [day, month, year, onChange]);

  // Generate days based on selected month and year
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month, 0).getDate();
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  if (!isOldAndroid) {
    // Use native date input for modern browsers
    return (
      <div className="relative">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Calendar className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="date"
            value={value}
            onChange={handleNativeChange}
            min={min}
            max={max}
            required={required}
            className={`pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${className}`}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  }

  // Generate arrays for days, months, and years
  const daysInMonth = month && year ? getDaysInMonth(parseInt(month), parseInt(year)) : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const dayNum = i + 1;
    return { value: dayNum.toString().padStart(2, '0'), label: dayNum.toString() };
  });

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = Array.from(
    { length: maxYear - minYear + 1 },
    (_, i) => {
      const yearNum = minYear + i;
      return { value: yearNum.toString(), label: yearNum.toString() };
    }
  );

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            required={required}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Day</option>
            {days.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="relative flex-1">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            required={required}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Month</option>
            {months.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="relative flex-1">
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            required={required}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Year</option>
            {years.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DateInput;